import type { PortalRole } from './auth/roles'

/**
 * Navegación del portal, filtrada por rol.
 *
 * Es la fuente única: el Sidebar dibuja los grupos, el Layout saca de acá el
 * título de la pantalla y la barra de sub-pestañas, y App.tsx registra las
 * rutas. Antes el título vivía en un mapa aparte dentro de Layout.tsx y se
 * desincronizaba cada vez que alguien agregaba una página.
 *
 * Reagrupación del rediseño de set-2026: el admin pasó de 18 ítems sueltos en
 * el sidebar a 6 módulos con sub-pestañas. La REGLA es que lo que antes era un
 * ítem del sidebar y ahora es sub-pestaña conserva su contenido íntegro y su
 * ruta: `/whatsapp` sigue siendo `/whatsapp`, solo cambia cómo se llega. Así
 * los links compartidos y los bookmarks siguen funcionando.
 */

/**
 * El ensayo del canal del socio solo existe en TEST. Se decide en tiempo de
 * build: el bundle de produccion ni siquiera contiene la pantalla.
 */
export const SOLO_TEST = import.meta.env.VITE_ENV_NAME === 'test'

export type IconName =
  | 'inbox' | 'expedientes' | 'performance' | 'templates' | 'dashboard' | 'operation'
  | 'documents' | 'metrics' | 'ai' | 'channels' | 'admin' | 'observability'

/** Una sub-pestaña de un módulo. Cada una es una ruta que ya existe. */
export interface SubTab {
  to: string
  label: string
  /** La pestaña ocupa todo el alto, sin el padding de página (ver NavItem). */
  fullBleed?: boolean
  /**
   * Subtítulo del header cuando esta pestaña está activa. Sobrescribe el del
   * módulo. Existe porque el subtítulo del módulo es genérico por definición
   * ("Parámetros de operación, accesos y costos") y cada pestaña tiene algo
   * concreto que decir — sobre todo las que explican una distinción que se
   * malinterpreta ("son dos permisos distintos").
   */
  sub?: string
  /** Contador opcional en pill (p. ej. documentos por revisar). */
  badge?: BadgeKey
  /** Solo admin, dentro de un módulo que el supervisor también ve. */
  adminOnly?: boolean
}

export type BadgeKey = 'inbox' | 'queue' | 'documents' | 'expedientes'

export interface NavItem {
  /** Ruta del ítem. Con sub-pestañas, es la de la primera. */
  to: string
  label: string
  icon: IconName
  /** Subtítulo del header de la pantalla. */
  sub: string
  badge?: BadgeKey
  tabs?: SubTab[]
  /**
   * La pantalla ocupa todo el alto sin el padding de página del Layout. Lo usa
   * la bandeja del asesor: es un grid de 3 columnas con scroll independiente
   * por columna, y el padding del main le robaría el alto que necesita.
   */
  fullBleed?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

// ── Asesor de telemarketing ──────────────────────────────────────────────────
//
// Tres ítems. NO ve cola global, otros asesores, configuración, integraciones,
// costos, usuarios ni auditoría: solo sus conversaciones asignadas. El recorte
// no es cosmético — `portal_api` lo aplica también en el servidor.

const ITEM_MI_BANDEJA: NavItem = {
  to: '/inbox',
  label: 'Mi bandeja',
  icon: 'inbox',
  sub: 'Tus conversaciones asignadas',
  badge: 'inbox',
  fullBleed: true,
}

/**
 * Los expedientes del asesor, de la entrega a la descarga (03-nov-2026). Lo
 * que el agente completó solo se ve acá sin vencer, y el número es lo que
 * espera que él lo descargue.
 */
const ITEM_MIS_EXPEDIENTES: NavItem = {
  to: '/mis-expedientes',
  label: 'Mis expedientes',
  icon: 'expedientes',
  sub: 'Tus expedientes: lo que el cliente entregó, quién lo cerró y si ya lo descargaste',
  badge: 'expedientes',
}

const NAV_ASESOR: NavGroup[] = [
  {
    label: 'OPERACIÓN',
    items: [ITEM_MI_BANDEJA, ITEM_MIS_EXPEDIENTES],
  },
  {
    label: 'HERRAMIENTAS',
    items: [
      {
        to: '/my-performance',
        label: 'Mi desempeño',
        icon: 'performance',
        sub: 'Tus indicadores y atenciones del día',
      },
      {
        to: '/templates',
        label: 'Plantillas y speech',
        icon: 'templates',
        sub: 'Plantillas aprobadas y guion vigente',
      },
    ],
  },
]

// ── Supervisor ───────────────────────────────────────────────────────────────

const TABS_OPERACION: SubTab[] = [
  { to: '/queue', label: 'Cola global', badge: 'queue' , sub: 'Conversaciones sin asignar que esperan a un asesor' },
  { to: '/live', label: 'En vivo', fullBleed: true,
    sub: 'Conversaciones del equipo en curso, en solo lectura; los documentos se validan o rechazan' },
  { to: '/advisors', label: 'Asesores' , sub: 'Carga, disponibilidad y orden de la rotación round-robin' },
  { to: '/conversations', label: 'Conversaciones' , sub: 'Bandeja del equipo — activas e históricas' },
  { to: '/expedientes', label: 'Expedientes',
    sub: 'El embudo: en qué fase está cada cliente que entró, con descarga en Excel' },
]

const TABS_DOCUMENTOS: SubTab[] = [
  { to: '/documents', label: 'Revisión', badge: 'documents' , sub: 'Revisión y aprobación de los documentos que envían los clientes' },
  { to: '/doc-validation', label: 'Validar detección' , sub: 'Confirmá o corregí el tipo que detectó el agente. Cada corrección mejora la medición y las reglas.' },
]

const ITEM_OPERACION: NavItem = {
  to: '/queue',
  label: 'Operación',
  icon: 'operation',
  sub: 'Cola del equipo, asesores y conversaciones',
  tabs: TABS_OPERACION,
}

const ITEM_DOCUMENTOS: NavItem = {
  to: '/documents',
  label: 'Documentos',
  icon: 'documents',
  sub: 'Revisión y aprobación de documentos',
  badge: 'documents',
  tabs: TABS_DOCUMENTOS,
}

/**
 * La entrada de supervisión y de Admin. Hasta el 28-set-2026 el supervisor
 * caía en la cola y el Admin en un Dashboard que respondía «¿cuánto hay?», con
 * la mitad de los números escritos en el código. El Panel responde «¿hay algo
 * que hacer?» y lleva a donde se hace.
 */
const ITEM_PANEL: NavItem = {
  to: '/',
  label: 'Panel',
  icon: 'dashboard',
  sub: 'Lo que necesita una persona ahora',
}

const NAV_SUPERVISOR: NavGroup[] = [
  { label: 'OPERACIÓN', items: [ITEM_PANEL, ITEM_OPERACION, ITEM_DOCUMENTOS] },
  {
    label: 'ANÁLISIS',
    items: [
      {
        to: '/metrics',
        label: 'Métricas',
        icon: 'metrics',
        sub: 'Indicadores del equipo y atención por asesor',
      },
    ],
  },
]

// ── Admin ────────────────────────────────────────────────────────────────────

const NAV_ADMIN: NavGroup[] = [
  {
    label: 'OPERACIÓN',
    items: [
      ITEM_PANEL,
      ITEM_OPERACION,
      ITEM_DOCUMENTOS,
    ],
  },
  {
    label: 'SALUD DEL SISTEMA',
    items: [
      {
        // El orden de las pestañas es el orden en que se investiga: primero qué
        // se rompió, después qué le pasó a ESE caso, y solo entonces el log
        // crudo. Nadie mira logs hasta que sabe que hay algo roto.
        to: '/incidents',
        label: 'Observabilidad',
        icon: 'observability',
        // Sin badge a propósito. El contador tendría que consultar CloudWatch,
        // SQS y DynamoDB en CADA carga del portal y en cada refresco del menú,
        // para todos los usuarios — caro para un número. La pantalla ya trae
        // sus indicadores arriba, que es donde se mira antes de actuar.
        sub: 'Qué falló, dónde y a quién le afectó',
        tabs: [
          { to: '/incidents', label: 'Incidencias', sub: 'Fallos detectados automáticamente y reportes de los asesores' },
          { to: '/traces', label: 'Trazas', sub: 'El recorrido completo de una conversación, paso a paso' },
          { to: '/logs', label: 'Logs', sub: 'Registro crudo de las Lambdas — solo Admin' },
          { to: '/audit', label: 'Auditoría', sub: 'Registro inmutable de eventos, con actor, fecha y acción' },
        ],
      },
    ],
  },
  {
    label: 'CONFIGURACIÓN',
    items: [
      {
        to: '/speech',
        label: 'Agente IA',
        icon: 'ai',
        sub: 'Guion, conocimiento y modelos del agente',
        tabs: [
          { to: '/speech', label: 'Speech' , sub: 'Lo que el agente le dice al cliente al empezar, según por dónde entra' },
          { to: '/knowledge', label: 'Conocimiento' , sub: 'Base de conocimiento que el agente consulta al responder' },
          { to: '/agents', label: 'Modelos' , sub: 'Modelos de Bedrock y política de ejecución' },
        ],
      },
      {
        to: '/whatsapp',
        label: 'Canales y CRM',
        icon: 'channels',
        sub: 'WhatsApp, integraciones y candidatos',
        tabs: [
          { to: '/whatsapp', label: 'WhatsApp' , sub: 'Número de negocio, estado y actividad del canal' },
          { to: '/integrations', label: 'Integraciones' , sub: 'Estado de HubSpot, n8n y Drive, con sus kill switches' },
          { to: '/candidates', label: 'Candidatos HubSpot' , sub: 'Leads importados de HubSpot, en modo lectura' },
          { to: '/agent-access', label: 'Acceso del agente' , sub: 'Quién puede escribirle al agente y a quién puede contactar. Son dos permisos distintos.' },
          { to: '/outreach', label: 'Contacto saliente' , sub: 'Mensajes a contactos de la lista permitida, con plantillas aprobadas' },
        ],
      },
      {
        to: '/config',
        label: 'Administración',
        icon: 'admin',
        sub: 'Parámetros de operación, accesos y costos',
        tabs: [
          { to: '/config', label: 'Configuración' , sub: 'Parámetros de operación del sistema' },
          { to: '/reglas-expedientes', label: 'Reglas de expedientes',
            sub: 'A quién le toca descargar un expediente completo, y quién ve qué' },
          { to: '/users', label: 'Usuarios' , sub: 'Usuarios registrados en el User Pool de Cognito, con su rol' },
          { to: '/costs', label: 'Costos y salud' , sub: 'Presupuesto, cuotas y desactivación de emergencia por componente' },
          { to: '/test-data', label: 'Datos de prueba' , sub: 'Usuarios ficticios para ensayar el agente, con la estructura de HubSpot' },
          // Solo TEST: registrar un lead hace que el bot le escriba a ese
          // numero. En PROD seria fabricar una precalificacion y mandarle un
          // WhatsApp a alguien que nunca hablo con nosotros. El backend corta
          // igual por ambiente; esconder la pestania es comodidad, no seguridad.
          ...(SOLO_TEST
            ? [{ to: '/test-leads', label: 'Leads de socio', sub: 'Numeros que entran por la API del socio, como entraran los de Software' },
               { to: '/test-reset', label: 'Reiniciar pruebas', sub: 'Deja TEST en cero para empezar una ronda limpia; conserva el historial del chat' }]
            : []),
        ],
      },
    ],
  },
]

const NAV_BY_ROLE: Record<PortalRole, NavGroup[]> = {
  asesor: NAV_ASESOR,
  supervisor: NAV_SUPERVISOR,
  admin: NAV_ADMIN,
}

/**
 * Navegación para el rol, más "Mi bandeja" si además está en el grupo Asesor.
 *
 * El rol efectivo es el de mayor alcance, pero la pertenencia al grupo Asesor
 * es lo que mira el round-robin: alguien que es Admin y Asesor a la vez RECIBE
 * conversaciones. Sin este agregado le caen casos y no tiene dónde verlos.
 */
export function navForRole(role: PortalRole | null, atiende = false): NavGroup[] {
  if (!role) return []
  const base = NAV_BY_ROLE[role]
  if (role === 'asesor' || !atiende) return base

  // Se inserta al principio del grupo OPERACIÓN, que ya existe en los dos
  // roles de mayor alcance: su bandeja es lo primero que mira quien atiende.
  return base.map((grupo) =>
    grupo.label === 'OPERACIÓN'
      ? { ...grupo, items: [ITEM_MI_BANDEJA, ...grupo.items] }
      : grupo,
  )
}

/** Ruta a la que cae cada rol al entrar. El asesor arranca en su bandeja. */
export function homeForRole(role: PortalRole | null): string {
  if (role === 'admin' || role === 'supervisor') return '/'
  return '/inbox'
}

/**
 * Resuelve la pantalla activa a partir del pathname.
 *
 * Devuelve el ítem del sidebar que hay que marcar activo (el módulo sigue
 * activo mientras cualquiera de sus sub-pestañas lo esté) y la pestaña
 * concreta. Se compara por prefijo para que `/conversations/abc-123` siga
 * resolviendo a `/conversations`.
 */
export interface ActiveScreen {
  item: NavItem | null
  tab: SubTab | null
  title: string
  sub: string
  tabs: SubTab[]
  fullBleed: boolean
}

function matches(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(to + '/')
}

export function resolveScreen(role: PortalRole | null, pathname: string,
                              atiende = false): ActiveScreen {
  const clean = pathname.replace(/\/+$/, '') || '/'

  for (const group of navForRole(role, atiende)) {
    for (const item of group.items) {
      // Las sub-pestañas se prueban primero: son más específicas que el `to`
      // del módulo, que apunta a la primera de ellas.
      for (const tab of item.tabs ?? []) {
        if (matches(clean, tab.to)) {
          return {
            item,
            tab,
            // El título es el del módulo; la pestaña ya se ve en la barra.
            title: item.label,
            sub: tab.sub ?? item.sub,
            tabs: item.tabs ?? [],
            fullBleed: tab.fullBleed ?? item.fullBleed ?? false,
          }
        }
      }
      if (matches(clean, item.to)) {
        return {
          item, tab: null, title: item.label, sub: item.sub,
          tabs: item.tabs ?? [], fullBleed: item.fullBleed ?? false,
        }
      }
    }
  }

  return { item: null, tab: null, title: 'Portal PGH', sub: '', tabs: [], fullBleed: false }
}
